import { extend } from "@dhx/ts-common/core";
import { awaitRedraw, create, el, inject, VNode } from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { fitPosition, toNode } from "@dhx/ts-common/html";
import { View } from "@dhx/ts-common/view";
import { IPopup, IPopupConfig, IPopupEventHandlersMap, IShowConfig, PopupEvents } from "./types";

export class Popup extends View implements IPopup {
	public config: IPopupConfig;
	public events: IEventSystem<PopupEvents, IPopupEventHandlersMap>;

	private _html: string;
	private _ui: any;

	private _popup: HTMLElement;

	private _clickEvent: (e: Event) => void;

	private _isActive: boolean;
	private _outerClickDestructor: () => void;

	private _timeout: any;

	constructor(config: IPopupConfig = {}) {
		super(null, extend({}, config));

		const popup = (this._popup = document.createElement("div"));
		popup.className = "dhx_widget dhx_popup" + (this.config.css ? " " + this.config.css : "");
		popup.style.position = "absolute";
		popup.setAttribute("role", "dialog");
		popup.setAttribute("aria-modal", "true");
		popup.setAttribute("aria-live", "polite");

		this.mount(
			popup,
			create({
				render: () => this.toVDOM(),
			})
		);

		this._clickEvent = e => this.events.fire(PopupEvents.click, [e]);

		this.events = config.events || new EventSystem<PopupEvents>(this);
		this._isActive = false;
	}
	show(node: HTMLElement, config: IShowConfig = {}, attached?: any): void {
		if (!this.events.fire(PopupEvents.beforeShow, [node])) {
			return;
		}
		node = toNode(node);
		if (this._isActive) {
			this._setPopupSize(node, config);
			return;
		}
		if (attached) {
			this.attach(attached);
		}

		this._popup.style.left = "0";
		this._popup.style.top = "0";

		awaitRedraw()
			.then(() => {
				this._setPopupSize(node, config);
				this._popup.style.position = "fixed";
				document.body.appendChild(this._popup);
				this._isActive = true;
			})
			.then(() => {
				this._popup.style.position = "absolute";
				this.events.fire(PopupEvents.afterShow, [node]);
				this._outerClickDestructor = this._detectOuterClick(node);
			});
	}
	hide(): void {
		this._hide(false, null);
	}
	isVisible(): boolean {
		return this._isActive;
	}
	attach(name: any, config?: any): VNode {
		this._html = null;
		if (typeof name === "object") {
			this._ui = name;
		} else if (typeof name === "string") {
			this._ui = new (window as any).dhx[name](null, config);
		} else if (typeof name === "function") {
			if (name.prototype instanceof View) {
				this._ui = new name(null, config);
			} else {
				this._ui = {
					getRootView() {
						return name(config);
					},
				};
			}
		}
		this.paint();
		return this._ui;
	}
	attachHTML(html: string): void {
		this._html = html;
		this.paint();
	}
	getWidget(): any {
		return this._ui;
	}
	getContainer(): HTMLElement {
		return this.getRootView().refs.content.el;
	}
	toVDOM() {
		let view: VNode;
		if (this._html) {
			view = el(".dhx_popup__inner-html-content", {
				".innerHTML": this._html,
			});
		} else {
			view = this._ui ? this._ui.getRootView() : null;
			if (view && view.render) {
				view = inject(view);
			}
		}
		return el(
			"div",
			{
				class: "dhx_popup-content",
				tabindex: 0,
				onclick: this._clickEvent,
				_key: this._uid,
				_ref: "content",
			},
			[view]
		);
	}
	destructor() {
		this.hide();
		if (this._outerClickDestructor) {
			this._outerClickDestructor();
		}
		this._popup = null;
	}
	private _setPopupSize(node: HTMLElement, config: IShowConfig, calls = 3) {
		const { width, height } = this._popup.getBoundingClientRect();
		// TODO: IE popup height = 0
		if (this._timeout) {
			clearTimeout(this._timeout);
			this._timeout = null;
		}
		if (calls && (width === 0 || height === 0)) {
			this._timeout = setTimeout(() => {
				if (!this._isActive) {
					return;
				}
				this._setPopupSize(node, config, calls - 1);
				this._timeout = null;
			});
			return;
		}

		const { left, top } = fitPosition(node, {
			centering: true,
			mode: "bottom",
			...config,
			width,
			height,
		});
		this._popup.style.left = left;
		this._popup.style.top = top;
		if (config.indent && config.indent !== 0) {
			switch (config.mode) {
				case "top":
					this._popup.style.top =
						parseInt(this._popup.style.top.slice(0, -2), null) -
						parseInt(config.indent.toString(), null) +
						"px";
					break;
				case "bottom":
					this._popup.style.top =
						parseInt(this._popup.style.top.slice(0, -2), null) +
						parseInt(config.indent.toString(), null) +
						"px";
					break;
				case "left":
					this._popup.style.left =
						parseInt(this._popup.style.left.slice(0, -2), null) -
						parseInt(config.indent.toString(), null) +
						"px";
					break;
				case "right":
					this._popup.style.left =
						parseInt(this._popup.style.left.slice(0, -2), null) +
						parseInt(config.indent.toString(), null) +
						"px";
					break;
				default:
					this._popup.style.top =
						parseInt(this._popup.style.top.slice(0, -2), null) +
						parseInt(config.indent.toString(), null) +
						"px";
					break;
			}
		}
	}
	private _detectOuterClick(node: HTMLElement) {
		const outerClick = (event: MouseEvent) => {
			let target = event.target;
			while (target) {
				if (target === node || target === this._popup) {
					return;
				}
				target = (target as any).parentNode;
			}
			if (this._hide(true, event)) {
				document.removeEventListener("mousedown", outerClick);
			}
		};
		document.addEventListener("mousedown", outerClick);
		return () => document.removeEventListener("mousedown", outerClick);
	}
	private _hide(fromOuterClick: boolean, e: Event) {
		if (this._isActive) {
			if (!this.events.fire(PopupEvents.beforeHide, [fromOuterClick, e])) {
				return false;
			}
			document.body.removeChild(this._popup);
			this._isActive = false;
			if (this._outerClickDestructor) {
				this._outerClickDestructor();
				this._outerClickDestructor = null;
			}
			this.events.fire(PopupEvents.afterHide, [e]);
			return true;
		}
	}
}
